import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { api, streamChat } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AccessGate } from "../components/AccessGate";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Send, LogOut, Sparkles, Utensils, HeartPulse, BookOpen, MessageSquare, Salad, CalendarDays, ShieldCheck, Download, KeyRound } from "lucide-react";
import jsPDF from "jspdf";

const CodexVaultCard = () => {
    const [vault, setVault] = useState([]);
    useEffect(() => {
        api.get("/codex/vault").then(({ data }) => setVault(data || []))
                                .catch(() => setVault([]));
    }, []);
    if (!vault.length) return null;
    const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    return (
        <SectionCard>
            <div className="flex items-center justify-between">
                <div>
                    <div className="font-mono-label text-gold">The Eve Codex · Your Vault</div>
                    <div className="text-xs text-sand/70 mt-1">All your purchased phase editions. Click to re-download anytime.</div>
                </div>
                <BookOpen className="w-5 h-5 text-gold" />
            </div>
            <div className="mt-4 space-y-2">
                {vault.map((p) => (
                    <a key={p.id || p.payment_id}
                       href={`${API_BASE}/api/codex/download/${p.download_token}`}
                       target="_blank" rel="noreferrer"
                       data-testid={`vault-dl-${p.payment_id || p.order_id}`}
                       className="flex items-center justify-between rounded-xl border border-gold/25 bg-gold/5 px-4 py-3 hover:bg-gold/10 transition-colors">
                        <div>
                            <div className="text-ivory text-sm">The Eve Codex · {p.plan === "lifetime" ? "Lifetime access" : "Monthly pass"}</div>
                            <div className="text-[11px] text-sand/60 mt-0.5">
                                Purchased {new Date(p.paid_at).toLocaleDateString("en-IN")} · Payment {p.payment_id?.slice(-10)}
                            </div>
                        </div>
                        <div className="flex items-center gap-1 text-gold text-xs">
                            <Download className="w-4 h-4" /> Download PDF
                        </div>
                    </a>
                ))}
            </div>
        </SectionCard>
    );
};

const SectionCard = ({ children, className = "" }) => (
    <div className={`card-noir p-6 lg:p-8 ${className}`}>{children}</div>
);

const MemberHomeCard = () => {
    const [sub, setSub] = useState(null);
    const [meas, setMeas] = useState([]);
    useEffect(() => {
        (async () => {
            try {
                const [s, m] = await Promise.all([
                    api.get("/me/subscription").catch(() => ({ data: {} })),
                    api.get("/me/measurements").catch(() => ({ data: [] })),
                ]);
                setSub(s.data && s.data.id ? s.data : null);
                setMeas(m.data || []);
            } catch (err) { console.warn("MemberHomeCard: /me endpoints unavailable", err); }
        })();
    }, []);
    const daysLeft = useMemo(() => {
        if (!sub?.end_date) return null;
        const end = new Date(sub.end_date + "T00:00:00Z");
        const now = new Date();
        return Math.round((end - now) / 86400000);
    }, [sub]);
    const spark = useMemo(() =>
        [...meas].reverse().map((r) => ({ d: r.date, weight: r.weight }))
                          .filter((x) => x.weight != null), [meas]);
    const latest = meas[0];
    if (!sub && !latest) return null;
    return (
        <div className="card-noir p-6 lg:p-8 grid lg:grid-cols-3 gap-5 mb-6" data-testid="member-home-card">
            <div className="lg:col-span-1">
                <div className="font-mono-label text-gold">Active plan</div>
                {sub ? (
                    <>
                        <div className="font-display text-2xl text-ivory mt-2">{sub.plan_name}</div>
                        <div className="text-xs text-sand/70 mt-1">Trainer: {sub.trainer_name || "—"}</div>
                        <div className="text-sm text-sand mt-3">Ends <span className="text-gold">{sub.end_date}</span></div>
                        {daysLeft != null && (
                            <div className={`text-xs mt-1 ${daysLeft < 0 ? "text-crimson" : daysLeft <= 7 ? "text-gold" : "text-sand/70"}`}>
                                {daysLeft < 0 ? `${Math.abs(daysLeft)}d overdue` :
                                 daysLeft === 0 ? "expires today" : `${daysLeft} days left`}
                                {sub.status === "frozen" && " · frozen"}
                            </div>
                        )}
                    </>
                ) : (
                    <div className="text-sand text-sm mt-3">No active plan on file. Contact reception to enroll.</div>
                )}
            </div>
            <div className="lg:col-span-1">
                <div className="font-mono-label text-gold">Latest measurement</div>
                {latest ? (
                    <>
                        <div className="font-display text-2xl text-ivory mt-2">
                            {latest.weight != null ? `${latest.weight} ${latest.unit_weight}` : "—"}
                        </div>
                        <div className="text-xs text-sand/70 mt-1">{latest.date}</div>
                        <div className="text-xs text-sand mt-3 space-x-3">
                            {latest.chest != null && <span>Chest {latest.chest}</span>}
                            {latest.waist != null && <span>Waist {latest.waist}</span>}
                            {latest.hips != null && <span>Hips {latest.hips}</span>}
                        </div>
                    </>
                ) : (
                    <div className="text-sand text-sm mt-3">Ask your coach to log a baseline measurement.</div>
                )}
            </div>
            <div className="lg:col-span-1">
                <div className="font-mono-label text-gold">Weight progress</div>
                {spark.length >= 2 ? (
                    <div className="h-24 mt-2" data-testid="member-progress-sparkline">
                        <ResponsiveContainer>
                            <LineChart data={spark} margin={{ top: 6, right: 6, bottom: 0, left: 0 }}>
                                <XAxis dataKey="d" hide />
                                <YAxis hide domain={["dataMin - 1", "dataMax + 1"]} />
                                <Tooltip contentStyle={{ background: "#121016", border: "1px solid #D4AF37", color: "#FAF5E9", fontSize: 11 }} />
                                <Line type="monotone" dataKey="weight" stroke="#D4AF37" strokeWidth={2} dot={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                ) : (
                    <div className="text-sand text-sm mt-3">Log at least two measurements to see the sparkline.</div>
                )}
                <div className="text-[10px] text-sand/60 mt-1">Over your last {spark.length} entries</div>
            </div>
        </div>
    );
};

const Overview = ({ user, phase, metrics, meals }) => {
    const todayKcal = meals.reduce((s, m) => s + (m.calories || 0), 0);
    return (
        <>
            <MemberHomeCard />
            <CodexVaultCard />
        <div className="grid lg:grid-cols-3 gap-6">
            <SectionCard className="lg:col-span-2">
                <div className="font-mono-label">Welcome back</div>
                <h2 className="font-display text-4xl mt-2 text-ivory">{user?.name?.split(" ")[0] || "Eve"} &mdash; you look fierce today.</h2>
                <p className="text-sand mt-3">Your body is in the <span className="text-gold">{phase?.phase || "—"}</span> phase{phase?.day ? ` · Day ${phase.day}` : ""}. Coach Eve has curated your day around it.</p>
                <div className="grid sm:grid-cols-3 gap-4 mt-8">
                    <div className="rounded-2xl border border-gold/15 p-4">
                        <div className="font-mono-label">Cycle Phase</div>
                        <div className="font-display text-2xl text-gold mt-1">{phase?.phase || "Set up"}</div>
                    </div>
                    <div className="rounded-2xl border border-gold/15 p-4">
                        <div className="font-mono-label">Today's kCal</div>
                        <div className="font-display text-2xl text-ivory mt-1">{todayKcal}</div>
                    </div>
                    <div className="rounded-2xl border border-gold/15 p-4">
                        <div className="font-mono-label">Logs</div>
                        <div className="font-display text-2xl text-ivory mt-1">{metrics.length}</div>
                    </div>
                </div>
            </SectionCard>
            <SectionCard>
                <div className="font-mono-label">Daily mantra</div>
                <p className="font-display text-2xl mt-3 text-ivory leading-snug">&ldquo;Rest is not weakness. It is strategy.&rdquo;</p>
                {/* motto */}
                <div className="mt-6 text-sand text-sm">Try the AI wellness chat for phase-based guidance.</div>
            </SectionCard>
        </div>
        </>
    );
};

const CycleTab = ({ phase, refresh }) => {
    const [date, setDate] = useState(new Date());
    const [len, setLen] = useState(28);
    const [plen, setPlen] = useState(5);

    const save = async () => {
        await api.post("/cycle", { period_start: date.toISOString().slice(0, 10), cycle_length: Number(len), period_length: Number(plen) });
        toast.success("Cycle updated");
        refresh();
    };
    return (
        <div className="grid lg:grid-cols-2 gap-6">
            <SectionCard>
                <div className="font-mono-label">Log Period Start</div>
                <div className="mt-4 rounded-xl border border-gold/15 p-2 inline-block">
                    <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} className="text-ivory" />
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                    <div><Label>Cycle length (days)</Label><Input type="number" value={len} onChange={(e) => setLen(e.target.value)} data-testid="cycle-length-input" /></div>
                    <div><Label>Period length (days)</Label><Input type="number" value={plen} onChange={(e) => setPlen(e.target.value)} data-testid="period-length-input" /></div>
                </div>
                <Button onClick={save} className="btn-gold mt-5" data-testid="cycle-save-btn">Save cycle</Button>
            </SectionCard>
            <SectionCard>
                <div className="font-mono-label">Current phase</div>
                <div className="mt-3 font-display text-5xl gold-text">{phase?.phase || "—"}</div>
                <div className="text-sand mt-2">Day {phase?.day || "—"} of {phase?.length || 28}</div>
                <div className="mt-6 space-y-3 text-sand text-sm">
                    <p><span className="text-gold">Follicular:</span> peak strength — lift heavy, learn skills.</p>
                    <p><span className="text-gold">Ovulatory:</span> HIIT & group energy shine.</p>
                    <p><span className="text-crimson">Luteal:</span> pilates, complex carbs, mindful mood.</p>
                    <p><span className="text-crimson">Menstrual:</span> restorative movement, iron-rich meals.</p>
                </div>
            </SectionCard>
        </div>
    );
};

const HealthTab = ({ metrics, refresh }) => {
    const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), weight: "", body_fat: "", muscle_mass: "", waist: "", hips: "", chest: "" });
    const save = async () => {
        const payload = { ...form };
        Object.keys(payload).forEach((k) => { if (k !== "date" && payload[k] === "") delete payload[k]; else if (k !== "date") payload[k] = Number(payload[k]); });
        await api.post("/metrics", payload);
        toast.success("Metric logged");
        setForm({ ...form, weight: "", body_fat: "", muscle_mass: "", waist: "", hips: "", chest: "" });
        refresh();
    };
    const chartData = metrics.filter((m) => m.weight).map((m) => ({ date: m.date, weight: m.weight }));
    return (
        <div className="grid lg:grid-cols-3 gap-6">
            <SectionCard className="lg:col-span-2">
                <div className="font-mono-label">Weight trend</div>
                <div className="h-72 mt-4">
                    {chartData.length ? (
                        <ResponsiveContainer><LineChart data={chartData}>
                            <XAxis dataKey="date" stroke="#8A8275" fontSize={11} /><YAxis stroke="#8A8275" fontSize={11} />
                            <Tooltip contentStyle={{ background: "#121016", border: "1px solid #D4AF37", borderRadius: 8, color: "#FAF5E9" }} />
                            <Line type="monotone" dataKey="weight" stroke="#D4AF37" strokeWidth={2} dot={{ r: 4, fill: "#DC143C" }} />
                        </LineChart></ResponsiveContainer>
                    ) : <div className="h-full grid place-items-center text-sand/70">Log measurements to see your trend</div>}
                </div>
            </SectionCard>
            <SectionCard>
                <div className="font-mono-label">Log measurement</div>
                <div className="grid grid-cols-2 gap-3 mt-4">
                    <div className="col-span-2"><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="metric-date" /></div>
                    <div><Label>Weight (kg)</Label><Input value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} data-testid="metric-weight" /></div>
                    <div><Label>Body fat %</Label><Input value={form.body_fat} onChange={(e) => setForm({ ...form, body_fat: e.target.value })} /></div>
                    <div><Label>Muscle (kg)</Label><Input value={form.muscle_mass} onChange={(e) => setForm({ ...form, muscle_mass: e.target.value })} /></div>
                    <div><Label>Waist (cm)</Label><Input value={form.waist} onChange={(e) => setForm({ ...form, waist: e.target.value })} /></div>
                    <div><Label>Hips (cm)</Label><Input value={form.hips} onChange={(e) => setForm({ ...form, hips: e.target.value })} /></div>
                    <div><Label>Chest (cm)</Label><Input value={form.chest} onChange={(e) => setForm({ ...form, chest: e.target.value })} /></div>
                </div>
                <Button onClick={save} className="btn-gold w-full mt-5" data-testid="metric-save-btn">Save</Button>
            </SectionCard>
        </div>
    );
};

const MOODS = ["Empowered", "Energetic", "Fatigued", "Anxious", "Calm"];
const JournalTab = ({ entries, refresh, phase }) => {
    const [content, setContent] = useState("");
    const [mood, setMood] = useState("Empowered");
    const save = async () => {
        if (!content.trim()) return;
        await api.post("/journal", { date: new Date().toISOString().slice(0, 10), mood, cycle_day: phase?.day || null, content });
        toast.success("Journal saved");
        setContent(""); refresh();
    };
    return (
        <div className="grid lg:grid-cols-3 gap-6">
            <SectionCard className="lg:col-span-2">
                <div className="font-mono-label">New entry</div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                    <div><Label>Mood</Label>
                        <Select value={mood} onValueChange={setMood}>
                            <SelectTrigger data-testid="journal-mood-trigger"><SelectValue /></SelectTrigger>
                            <SelectContent>{MOODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div><Label>Cycle day</Label><Input value={phase?.day || ""} disabled /></div>
                </div>
                <Label className="mt-4 block">Write freely</Label>
                <Textarea rows={6} value={content} onChange={(e) => setContent(e.target.value)} placeholder="What is she feeling today?" data-testid="journal-content" />
                <Button onClick={save} className="btn-gold mt-4" data-testid="journal-save-btn">Save entry</Button>
            </SectionCard>
            <SectionCard>
                <div className="font-mono-label">Recent</div>
                <div className="mt-4 space-y-3 max-h-[500px] overflow-y-auto pr-2">
                    {entries.length === 0 && <div className="text-sand/70 text-sm">No entries yet.</div>}
                    {entries.map((e) => (
                        <div key={e.id} className="rounded-xl border border-gold/15 p-4" data-testid={`journal-entry-${e.id}`}>
                            <div className="flex justify-between text-xs">
                                <span className="text-gold">{e.mood}</span><span className="text-sand/70">{e.date}</span>
                            </div>
                            <p className="text-sm text-ivory mt-2 whitespace-pre-wrap">{e.content}</p>
                        </div>
                    ))}
                </div>
            </SectionCard>
        </div>
    );
};

const MEALS = ["breakfast", "lunch", "snack", "dinner"];
const CaloriesTab = ({ meals, refresh }) => {
    const [f, setF] = useState({ name: "", calories: "", protein: "", carbs: "", fat: "", meal_type: "breakfast" });
    const today = new Date().toISOString().slice(0, 10);
    const totals = useMemo(() => meals.reduce((a, m) => ({
        kcal: a.kcal + (m.calories || 0), p: a.p + (m.protein || 0), c: a.c + (m.carbs || 0), fat: a.fat + (m.fat || 0),
    }), { kcal: 0, p: 0, c: 0, fat: 0 }), [meals]);
    const macroData = [
        { name: "Protein", value: totals.p, color: "#D4AF37" },
        { name: "Carbs", value: totals.c, color: "#DC143C" },
        { name: "Fat", value: totals.fat, color: "#8B0000" },
    ];
    const save = async () => {
        if (!f.name || !f.calories) return;
        await api.post("/meals", { ...f, date: today, calories: Number(f.calories), protein: Number(f.protein || 0), carbs: Number(f.carbs || 0), fat: Number(f.fat || 0) });
        toast.success("Meal logged"); setF({ name: "", calories: "", protein: "", carbs: "", fat: "", meal_type: "breakfast" }); refresh();
    };
    return (
        <div className="grid lg:grid-cols-3 gap-6">
            <SectionCard>
                <div className="font-mono-label">Add meal</div>
                <div className="mt-4 space-y-3">
                    <Input placeholder="Meal name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="meal-name" />
                    <div className="grid grid-cols-2 gap-3">
                        <Input placeholder="Calories" value={f.calories} onChange={(e) => setF({ ...f, calories: e.target.value })} data-testid="meal-calories" />
                        <Select value={f.meal_type} onValueChange={(v) => setF({ ...f, meal_type: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{MEALS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input placeholder="Protein g" value={f.protein} onChange={(e) => setF({ ...f, protein: e.target.value })} />
                        <Input placeholder="Carbs g" value={f.carbs} onChange={(e) => setF({ ...f, carbs: e.target.value })} />
                        <Input placeholder="Fat g" value={f.fat} onChange={(e) => setF({ ...f, fat: e.target.value })} />
                    </div>
                    <Button onClick={save} className="btn-gold w-full" data-testid="meal-save-btn">Log</Button>
                </div>
            </SectionCard>
            <SectionCard>
                <div className="font-mono-label">Macros today</div>
                <div className="h-56 mt-3">
                    <ResponsiveContainer><PieChart>
                        <Pie data={macroData.every((d) => !d.value) ? [{ name: "empty", value: 1, color: "#2B2634" }] : macroData} dataKey="value" innerRadius={55} outerRadius={80} stroke="none">
                            {(macroData.every((d) => !d.value) ? [{ color: "#2B2634", name: "empty" }] : macroData).map((d) => <Cell key={d.name} fill={d.color} />)}
                        </Pie>
                        <Tooltip contentStyle={{ background: "#121016", border: "1px solid #D4AF37", color: "#FAF5E9" }} />
                    </PieChart></ResponsiveContainer>
                </div>
                <div className="text-center font-display text-3xl gold-text">{totals.kcal} kCal</div>
                <div className="text-center text-sand text-sm">P {Math.round(totals.p)} · C {Math.round(totals.c)} · F {Math.round(totals.fat)}</div>
            </SectionCard>
            <SectionCard>
                <div className="font-mono-label">Today's log</div>
                <div className="mt-4 space-y-2 max-h-[380px] overflow-y-auto pr-2">
                    {meals.length === 0 && <div className="text-sand/70 text-sm">Nothing yet.</div>}
                    {meals.map((m) => (
                        <div key={m.id} className="flex justify-between rounded-xl border border-gold/15 p-3" data-testid={`meal-${m.id}`}>
                            <div><div className="text-ivory">{m.name}</div><div className="text-xs text-sand/70 capitalize">{m.meal_type}</div></div>
                            <div className="text-gold font-mono text-sm">{m.calories}</div>
                        </div>
                    ))}
                </div>
            </SectionCard>
        </div>
    );
};

const NutritionTab = ({ phase }) => {
    const [data, setData] = useState(null);
    useEffect(() => {
        if (!phase?.phase) return;
        api.get(`/nutrition/${phase.phase}`).then((r) => setData(r.data));
    }, [phase?.phase]);

    const downloadPdf = () => {
        if (!data || !phase?.phase) return;
        const doc = new jsPDF({ unit: "pt", format: "a4" });
        const W = doc.internal.pageSize.getWidth();
        const H = doc.internal.pageSize.getHeight();
        const OBS = [9, 8, 10];
        const GOLD = [212, 175, 55];
        const CRIMSON = [220, 20, 60];
        const IVORY = [250, 245, 233];
        const SAND = [200, 191, 176];
        const MUTED = [138, 130, 117];

        const bg = () => {
            doc.setFillColor(...OBS); doc.rect(0, 0, W, H, "F");
            doc.setFillColor(...GOLD); doc.rect(40, 40, 6, H - 80, "F");
        };
        const eyebrow = (t, y) => {
            doc.setFont("helvetica", "bold"); doc.setFontSize(9);
            doc.setTextColor(...GOLD); doc.text(t.toUpperCase(), 70, y, { charSpace: 2 });
        };
        const title = (t, y, size = 30) => {
            doc.setFont("times", "normal"); doc.setFontSize(size);
            doc.setTextColor(...IVORY); doc.text(t, 70, y);
        };
        const body = (t, y, maxW = W - 130, size = 12) => {
            doc.setFont("helvetica", "normal"); doc.setFontSize(size); doc.setTextColor(...SAND);
            const lines = doc.splitTextToSize(t, maxW);
            doc.text(lines, 70, y);
            return y + lines.length * (size + 4);
        };
        const rule = (y) => { doc.setDrawColor(...GOLD); doc.setLineWidth(0.5); doc.line(70, y, W - 60, y); };
        const footer = () => {
            doc.setTextColor(...MUTED); doc.setFontSize(9); doc.setFont("helvetica", "normal");
            doc.text("All About Eve Fitness  ·  SR Nagar, Hyderabad  ·  +91 70326 90303", W / 2, H - 40, { align: "center" });
            doc.text("Not medical advice. Consult your physician for medical concerns.", W / 2, H - 26, { align: "center" });
        };
        const card = (label, value, y) => {
            doc.setDrawColor(43, 38, 52); doc.roundedRect(70, y, W - 130, 52, 8, 8);
            doc.setTextColor(...IVORY); doc.setFont("times", "normal"); doc.setFontSize(15);
            doc.text(label, 88, y + 22);
            doc.setTextColor(...GOLD); doc.setFont("helvetica", "normal"); doc.setFontSize(11);
            doc.text(value, W - 88, y + 22, { align: "right" });
            return y + 64;
        };

        // Phase-specific beginner workouts
        const WORKOUTS = {
            Menstrual: [
                ["Cat–Cow flow", "8 reps · slow"],
                ["Child's pose + deep breathing", "3 min"],
                ["Gentle glute bridge", "2 × 10"],
                ["Wall sit (light)", "2 × 20 sec"],
                ["Legs-up-the-wall recovery", "5 min"],
            ],
            Follicular: [
                ["Bodyweight squats", "3 × 12"],
                ["Push-ups (knees ok)", "3 × 10"],
                ["Dumbbell rows (2–5 kg)", "3 × 10 / side"],
                ["Kickboxing shadow rounds", "3 × 90 sec"],
                ["Plank hold", "3 × 30 sec"],
            ],
            Ovulatory: [
                ["Jumping jacks", "3 × 30 sec"],
                ["Burpees (step-back)", "3 × 8"],
                ["Zumba warm flow", "10 min"],
                ["Aerial-yoga inversion prep", "5 min"],
                ["Skater lunges", "3 × 12"],
            ],
            Luteal: [
                ["Reformer / mat pilates", "20 min"],
                ["Goblet squat (light)", "3 × 10"],
                ["Bird-dog", "3 × 8 / side"],
                ["Slow deadlift form drill", "3 × 8"],
                ["Restorative yoga wind-down", "8 min"],
            ],
        };
        const WHY = "Your hormones aren't a straight line — they rise and fall in a 28-day rhythm across four phases. Training and eating in tune with each phase means fewer injuries, better recovery, stronger PRs, calmer moods and steadier energy. This is the Eve method — not one workout for every week, but the right workout for this week.";
        const PHASE_INTRO = {
            Menstrual: "Low estrogen and low progesterone. Energy dips, iron stores are lower. Move gently and restore — that IS training this week.",
            Follicular: "Estrogen climbs. Peak learning, strength and confidence. This is her heavy-lift, skill-sharpening, brave-goal week.",
            Ovulatory: "Estrogen peaks then falls. Explosive power and endurance shine. High-intensity, group energy, and dance are her friends.",
            Luteal: "Progesterone rises. Body craves comfort carbs and steadiness. Slow strength, pilates and complex meals keep her balanced.",
        };

        // ---------- PAGE 1 — Cover ----------
        bg();
        doc.setTextColor(...GOLD); doc.setFont("helvetica", "bold"); doc.setFontSize(9);
        doc.text("THE EVE CYCLE PLAYBOOK", 70, 72, { charSpace: 3 });
        doc.setFont("times", "italic"); doc.setFontSize(11); doc.setTextColor(...MUTED);
        doc.text("Volume · " + phase.phase, 70, 92);
        title("Train & eat", 170, 44);
        title("with her rhythm.", 210, 44);
        doc.setTextColor(...CRIMSON); doc.setFont("times", "italic"); doc.setFontSize(20);
        doc.text("A beginner's guide to cycle-synced fitness.", 70, 250);
        rule(280);
        doc.setTextColor(...SAND); doc.setFont("helvetica", "normal"); doc.setFontSize(11);
        doc.text([
            `Prepared for  ·  Day ${phase.day || "—"} of ${phase.length || 28}`,
            `Current phase  ·  ${phase.phase}`,
            `Studio  ·  All About Eve Fitness, Hyderabad`,
        ], 70, 310, { lineHeightFactor: 1.6 });
        // Cover crimson pill
        doc.setFillColor(...CRIMSON); doc.roundedRect(70, 420, 200, 40, 20, 20, "F");
        doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(11);
        doc.text("INSIDE  ·  4 SECTIONS", 170, 445, { align: "center", charSpace: 2 });
        doc.setTextColor(...IVORY); doc.setFont("times", "normal"); doc.setFontSize(13);
        doc.text([
            "01   Why cycle-syncing matters",
            "02   The four phases at a glance",
            "03   Beginner workout for " + phase.phase,
            "04   Beginner meal plan for " + phase.phase,
        ], 70, 500, { lineHeightFactor: 1.7 });
        footer();

        // ---------- PAGE 2 — Why it matters ----------
        doc.addPage(); bg();
        eyebrow("01  ·  The Why", 72);
        title("Why cycle-syncing matters.", 110, 26);
        let y2 = body(WHY, 150);
        y2 += 20;
        eyebrow("The four phases", y2);
        y2 += 25;
        const PHASES = [
            ["Menstrual  ·  Day 1–5", "Restore. Iron-rich meals, gentle mobility."],
            ["Follicular  ·  Day 6–13", "Build. Heavy lifts, kickboxing, learn skills."],
            ["Ovulatory  ·  Day 14–16", "Peak. HIIT, Zumba, group energy."],
            ["Luteal  ·  Day 17–28", "Steady. Pilates, complex carbs, mindful moods."],
        ];
        PHASES.forEach(([label, note]) => { y2 = card(label, note, y2); });
        footer();

        // ---------- PAGE 3 — Beginner workout ----------
        doc.addPage(); bg();
        eyebrow("03  ·  Beginner workout", 72);
        title(`${phase.phase} — move like her body wants.`, 110, 24);
        body(PHASE_INTRO[phase.phase] || "", 150);
        let y3 = 210;
        eyebrow("Warm-up  ·  5 min", y3); y3 += 20;
        y3 = body("Neck rolls · Shoulder circles · Hip circles · 3-min brisk walk in place.", y3, W - 130, 12);
        y3 += 10;
        eyebrow("The workout", y3); y3 += 20;
        (WORKOUTS[phase.phase] || []).forEach((row) => { y3 = card(row[0], row[1], y3); });
        y3 += 6;
        eyebrow("Cool-down  ·  5 min", y3); y3 += 20;
        y3 = body("Forward fold · Seated twist · 4-7-8 breathing × 6 rounds.", y3, W - 130, 12);
        footer();

        // ---------- PAGE 4 — Beginner meal plan ----------
        doc.addPage(); bg();
        eyebrow("04  ·  Beginner meal plan", 72);
        title(`${phase.phase} plate — real Indian meals.`, 110, 24);
        body(data.focus, 150);
        let y4 = 200;
        eyebrow("Today's suggested meals", y4); y4 += 20;
        data.meals.forEach((m, i) => { y4 = card(`0${i + 1}   ${m.name}`, `${m.kcal} kcal`, y4); });
        y4 += 8;
        eyebrow("Hydration & habits", y4); y4 += 20;
        y4 = body(
            "· Aim for 2.5–3 L water (add lemon + rock salt if energy is low).\n" +
            "· 20 min of morning sunlight resets circadian + hormonal rhythm.\n" +
            "· 7–9 hours of sleep is non-negotiable in the luteal & menstrual phases.",
            y4, W - 130, 12
        );
        footer();

        doc.save(`AAE_CyclePlaybook_${phase.phase}.pdf`);
        toast.success("Cycle Playbook downloaded");
    };

    return (
        <SectionCard>
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="font-mono-label">Phase-synced nutrition</div>
                    <h3 className="font-display text-3xl gold-text mt-3">{phase?.phase || "Set your cycle first"}</h3>
                </div>
                {data && phase?.phase && (
                    <Button onClick={downloadPdf} className="btn-gold" data-testid="download-mealplan-btn">
                        <Download className="w-4 h-4 mr-2" /> Download Cycle Playbook (PDF)
                    </Button>
                )}
            </div>
            {data && <>
                <p className="text-sand mt-2">{data.focus}</p>
                <div className="grid md:grid-cols-2 gap-4 mt-6">
                    {data.meals.map((m, i) => (
                        <div key={m.name} className="rounded-xl border border-gold/20 p-5 flex justify-between items-center" data-testid={`nutrition-meal-${i}`}>
                            <div className="text-ivory font-display text-lg">{m.name}</div>
                            <div className="text-gold font-mono">{m.kcal} kcal</div>
                        </div>
                    ))}
                </div>
            </>}
        </SectionCard>
    );
};

const ChatTab = () => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [sub, setSub] = useState(null);
    const endRef = useRef(null);

    useEffect(() => {
        api.get("/chat/history").then((r) => setMessages(r.data.map((m) => ({ id: m.id || `${m.created_at}-${m.role}`, role: m.role, content: m.content })))).catch((err) => { console.warn("chat history load failed", err); });
        api.get("/me/subscription").then(({ data }) => setSub(data && data.id ? data : null)).catch(() => setSub(null));
    }, []);
    useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

    const send = async () => {
        if (!input.trim() || busy) return;
        const msg = input; setInput(""); setBusy(true);
        const uid = `u-${Date.now()}`;
        const aid = `a-${Date.now()}`;
        setMessages((m) => [...m, { id: uid, role: "user", content: msg }, { id: aid, role: "assistant", content: "" }]);
        try {
            await streamChat(msg, (delta) => {
                setMessages((m) => {
                    const copy = [...m]; copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + delta }; return copy;
                });
            });
        } catch (err) { console.error("chat stream failed", err); toast.error("Eve is momentarily quiet — try again."); }
        setBusy(false);
    };
    const quick = [
        "How does the follicular phase feel?",
        "What classes do you offer?",
        "General PCOS tips",
    ];

    const showUpsell = messages.length >= 2 && !sub;

    return (
        <SectionCard className="flex flex-col h-[70vh]">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="font-mono-label">Eve — your wellness concierge</div>
                <div className="text-[10px] text-sand/60">
                    General guidance is free · personalised plans require a membership or the Codex
                </div>
            </div>
            <div className="flex-1 overflow-y-auto mt-4 space-y-3 pr-2" data-testid="chat-messages">
                {messages.length === 0 && (
                    <div className="text-sand/70 text-sm">
                        Ask Eve about the cycle phases, our classes, or general wellness. For a
                        <span className="text-gold"> personalised protocol</span>, you'll be nudged
                        to the Codex or a 1:1 with our coach.
                    </div>
                )}
                {messages.map((m, i) => (
                    <div key={m.id || i} className={`max-w-[80%] rounded-2xl p-4 ${m.role === "user" ? "bg-gold/15 border border-gold/40 ml-auto" : "bg-obsidian-light border border-gold/15"}`}>
                        <div className="text-xs font-mono-label mb-1">{m.role === "user" ? "You" : "Eve"}</div>
                        <div className="text-ivory whitespace-pre-wrap text-sm">{m.content || (busy && i === messages.length - 1 ? "…" : "")}</div>
                    </div>
                ))}
                {showUpsell && (
                    <div className="rounded-xl border border-gold/40 bg-gradient-to-br from-crimson/10 to-gold/10 p-4 max-w-[85%]"
                         data-testid="chat-upsell">
                        <div className="font-mono-label !text-[10px] text-gold">READY FOR A REAL PLAN?</div>
                        <div className="text-ivory text-sm mt-1">
                            Eve keeps it high-level here on purpose. For a phase-by-phase workout &amp;
                            meal protocol built around <em>your</em> cycle, unlock the Eve Codex
                            (₹899 lifetime) or book a 1:1 with our head coach.
                        </div>
                        <div className="flex gap-2 mt-3 flex-wrap">
                            <Link to="/#membership" className="text-xs px-3 py-1.5 rounded-full bg-gold text-obsidian font-semibold" data-testid="chat-upsell-codex">Unlock the Codex</Link>
                            <a href="tel:+917032690303" className="text-xs px-3 py-1.5 rounded-full border border-gold/50 text-gold hover:bg-gold/10" data-testid="chat-upsell-call">Call front desk</a>
                        </div>
                    </div>
                )}
                <div ref={endRef} />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
                {quick.map((q) => <button key={q} onClick={() => setInput(q)} className="text-xs px-3 py-1.5 rounded-full border border-gold/30 text-sand hover:bg-gold/10" data-testid={`chat-quick-${q.slice(0,10)}`}>{q}</button>)}
            </div>
            <div className="mt-3 flex gap-2">
                <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask Eve anything…" data-testid="chat-input" />
                <Button onClick={send} disabled={busy} className="btn-gold" data-testid="chat-send-btn"><Send className="w-4 h-4" /></Button>
            </div>
        </SectionCard>
    );
};

const PasswordCard = () => {
    const [status, setStatus] = useState(null);
    const [pw, setPw] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        api.get("/auth/password/status").then(({ data }) => setStatus(data))
                                         .catch(() => setStatus({ has_password: false }));
    }, []);
    const save = async () => {
        if (pw.length < 8) return toast.error("Password must be at least 8 characters");
        if (pw !== confirm) return toast.error("Passwords don't match");
        setBusy(true);
        try {
            await api.post("/auth/password/set", { password: pw });
            toast.success("Password saved");
            setStatus({ has_password: true }); setPw(""); setConfirm("");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save password");
        }
        setBusy(false);
    };
    if (status === null) return null;
    return (
        <SectionCard>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <KeyRound className="w-5 h-5 text-gold" />
                    <div>
                        <div className="font-mono-label text-gold">
                            {status.has_password ? "Change password" : "Set a password"}
                        </div>
                        <div className="text-xs text-sand/70 mt-1">
                            {status.has_password
                                ? "Pick a new password — you'll still be able to sign in with an email code."
                                : "Skip the OTP next time. 8+ characters, used alongside your email code."}
                        </div>
                    </div>
                </div>
            </div>
            <div className="grid md:grid-cols-3 gap-3 mt-4">
                <Input type="password" placeholder="New password (min 8)" value={pw}
                       onChange={(e) => setPw(e.target.value)} data-testid="dash-set-pw-new" />
                <Input type="password" placeholder="Confirm password" value={confirm}
                       onChange={(e) => setConfirm(e.target.value)} data-testid="dash-set-pw-confirm" />
                <Button onClick={save} disabled={busy || !pw || !confirm} className="btn-gold"
                        data-testid="dash-set-pw-save">
                    {busy ? "Saving…" : status.has_password ? "Update password" : "Set password"}
                </Button>
            </div>
        </SectionCard>
    );
};

export default function Dashboard() {
    const { user, logout } = useAuth();
    const [phase, setPhase] = useState(null);
    const [metrics, setMetrics] = useState([]);
    const [meals, setMeals] = useState([]);
    const [entries, setEntries] = useState([]);
    const today = new Date().toISOString().slice(0, 10);
    const load = useCallback(async () => {
        try {
            const [p, m, mm, j] = await Promise.all([
                api.get("/cycle/current"), api.get("/metrics"), api.get("/meals", { params: { date: today } }), api.get("/journal"),
            ]);
            setPhase(p.data); setMetrics(m.data); setMeals(mm.data); setEntries(j.data);
        } catch (err) {
            if (process.env.NODE_ENV !== "production") console.error("dashboard load failed", err);
        }
    }, [today]);
    useEffect(() => { load(); }, [load]);

    return (
        <AccessGate user={user}>
        <div className="min-h-screen bg-obsidian text-ivory">
            <Toaster theme="dark" richColors />
            <header className="section-x py-4 flex items-center justify-between border-b border-gold/15 sticky top-0 z-40 bg-obsidian/85 backdrop-blur-xl">
                <div className="flex items-center gap-3">
                    <img src="https://customer-assets-agu9un31.emergentagent.net/job_eve-fitness-hub/artifacts/mpla7sb8_AAE%20Logo.webp" alt="All About Eve" className="h-12 w-auto object-contain" />
                    <div className="hidden md:block font-mono-label !text-[10px] border-l border-gold/25 pl-3">Member · Studio</div>
                </div>
                <div className="flex items-center gap-3">
                    {user?.role === "admin" && <Link to="/admin" className="btn-ghost-gold text-sm" data-testid="dash-admin-link"><ShieldCheck className="inline w-4 h-4 mr-1"/> Admin CRM</Link>}
                    {user?.role === "staff" && <Link to="/admin" className="btn-ghost-gold text-sm" data-testid="dash-staff-link"><ShieldCheck className="inline w-4 h-4 mr-1"/> Staff CRM</Link>}
                    {user?.picture && <img src={user.picture} alt="" className="w-9 h-9 rounded-full border border-gold/40" />}
                    <span className="text-sm text-sand hidden md:block">{user?.name}</span>
                    <Button variant="ghost" onClick={logout} className="text-crimson hover:text-crimson" data-testid="logout-btn"><LogOut className="w-4 h-4"/></Button>
                </div>
            </header>
            <main className="section-x py-10">
                <Tabs defaultValue="overview" className="space-y-6">
                    <TabsList className="bg-obsidian-light border border-gold/15 flex-wrap h-auto">
                        <TabsTrigger value="overview" data-testid="tab-overview"><Sparkles className="w-4 h-4 mr-1"/>Overview</TabsTrigger>
                        <TabsTrigger value="cycle" data-testid="tab-cycle"><CalendarDays className="w-4 h-4 mr-1"/>Cycle</TabsTrigger>
                        <TabsTrigger value="health" data-testid="tab-health"><HeartPulse className="w-4 h-4 mr-1"/>Health</TabsTrigger>
                        <TabsTrigger value="journal" data-testid="tab-journal"><BookOpen className="w-4 h-4 mr-1"/>Journal</TabsTrigger>
                        <TabsTrigger value="calories" data-testid="tab-calories"><Utensils className="w-4 h-4 mr-1"/>Calories</TabsTrigger>
                        <TabsTrigger value="nutrition" data-testid="tab-nutrition"><Salad className="w-4 h-4 mr-1"/>Nutrition</TabsTrigger>
                        <TabsTrigger value="chat" data-testid="tab-chat"><MessageSquare className="w-4 h-4 mr-1"/>Ask Eve</TabsTrigger>
                    </TabsList>
                    <TabsContent value="overview"><Overview user={user} phase={phase} metrics={metrics} meals={meals} /></TabsContent>
                    <TabsContent value="cycle"><CycleTab phase={phase} refresh={load} /></TabsContent>
                    <TabsContent value="health"><HealthTab metrics={metrics} refresh={load} /></TabsContent>
                    <TabsContent value="journal"><JournalTab entries={entries} refresh={load} phase={phase} /></TabsContent>
                    <TabsContent value="calories"><CaloriesTab meals={meals} refresh={load} /></TabsContent>
                    <TabsContent value="nutrition"><NutritionTab phase={phase} /></TabsContent>
                    <TabsContent value="chat"><ChatTab /></TabsContent>
                </Tabs>
                <div className="mt-10"><PasswordCard /></div>
            </main>
        </div>
        </AccessGate>
    );
}
